import Box from '@mui/material/Box';
import React, { FC, useEffect, useState } from 'react';
import { TwButton, TwButtonType } from '../../../TwButtons/TwButtons';
import NumberUtils from '../../../../utils/numberUtils';
import TwTooltip from '../../../TwTooltip/TwTooltip';
import { PROVIDER } from '../../../../providers/User/User.model';
import BreakdownComponent from './BreakdownComponent';
import { MileageType } from './AddEditMiles';
import { useGetTripByIdQuery } from '../../../../redux/api/trips/tripApi';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getTooltipText } from '../../../../utils/mileage/mileageUtils';

interface TwTotalMilageProps {
  totalMileage: number;
  emptyMileage: number;
  loadedMileage: number;
  tooltipText: string;
  tripId: string;
  isDriver: boolean;
  isEmptyMilesEnabled: boolean;
  reloadData?: () => void;
  token?: string;
  editable?: boolean;
}

const TwTotalMilage: FC<TwTotalMilageProps> = ({
  totalMileage,
  emptyMileage,
  loadedMileage,
  tooltipText,
  tripId,
  reloadData,
  isDriver,
  isEmptyMilesEnabled,
  token,
  editable
}): JSX.Element => {
  const [show, setShow] = React.useState(false);
  const [tooltip, setTooltip] = useState<string[]>([]);
  const { data: tripData } = useGetTripByIdQuery(tripId, { skip: !tripId });

  const handleHideShow = (): void => {
    setShow(!show);
  };

  const emptyAndLoadedExist = !!loadedMileage && !!emptyMileage;
  const emptyAndLoadedExistOrIsDriver = (!!loadedMileage && !!emptyMileage) || isDriver;

  const showTitle = (): string => {
    if ((emptyAndLoadedExist && isEmptyMilesEnabled) || isDriver) {
      return 'Total miles';
    }
    if (!!loadedMileage && (!emptyMileage || !isEmptyMilesEnabled)) {
      return 'Loaded miles';
    }
    if (emptyMileage && !loadedMileage && isEmptyMilesEnabled) {
      return 'Empty miles';
    }
    return '';
  };

  useEffect(() => {
    if (!tripData) return;
    const text = [];

    if (loadedMileage > 0)
      text.push(
        getTooltipText(
          'Loaded miles',
          tripData.loaded_mileage_source || PROVIDER.GOOGLE,
          tripData.is_loaded_miles_auto
        )
      );

    if (emptyMileage > 0)
      text.push(
        getTooltipText(
          'Empty miles',
          tripData.empty_mileage_source || PROVIDER.GOOGLE,
          tripData.is_empty_miles_auto
        )
      );

    setTooltip(text);
  }, [
    tripData?.is_loaded_miles_auto,
    tripData?.loaded_mileage_source,
    tripData?.is_empty_miles_auto,
    tripData?.empty_mileage_source
  ]);

  return (
    <>
      <Box component="div" className="load-detail__data-container__overview--sub-title">
        {showTitle()}
        <TwTooltip title={tooltip.join('\n')} placement="top" sx={{ whiteSpace: 'pre' }}>
          <InfoOutlinedIcon sx={{ height: '16px' }} />
        </TwTooltip>
      </Box>
      <Box
        component="div"
        className="load-detail__data-container__overview__rate"
        sx={{
          paddingBottom:
            emptyAndLoadedExistOrIsDriver && isEmptyMilesEnabled ? '0 !important' : '1rem'
        }}
      >
        {NumberUtils.formatNumber(totalMileage, 2)} mi
      </Box>
      {((emptyAndLoadedExist && isEmptyMilesEnabled) || isDriver) && (
        <TwButton
          label={!show ? 'Show breakdown' : 'Hide breakdown'}
          action={handleHideShow}
          type={TwButtonType.text}
          sx={{
            width: '150px',
            padding: '0px',
            paddingBottom: '20px',
            justifyContent: 'flex-start',
            textTransform: 'none !important'
          }}
        />
      )}
      {show && (
        <>
          {(loadedMileage !== 0 || isDriver) && (
            <BreakdownComponent
              tripId={tripId}
              mileageType={MileageType.Loaded}
              mileage={loadedMileage}
              isDriver={isDriver}
              reloadData={reloadData}
              token={token}
              editable={editable}
            />
          )}
          {(emptyMileage !== 0 || isDriver) && isEmptyMilesEnabled && (
            <BreakdownComponent
              tripId={tripId}
              mileageType={MileageType.Empty}
              mileage={emptyMileage}
              isDriver={isDriver}
              tooltipText={tooltipText}
              reloadData={reloadData}
              token={token}
              editable={editable}
            />
          )}
        </>
      )}
    </>
  );
};

export default TwTotalMilage;
